'use client'

import { useEffect, useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { MdClose, MdDriveFileMove } from 'react-icons/md'
import { getPipelineMoveTargets, moveDealStage, PipelineMoveTarget } from '@/lib/api/sales'

interface MoveDealPipelineModalProps {
    /** Uma ou mais oportunidades: no modo Leads o card move todas as do lead naquela etapa. */
    dealIds: number[]
    dealTitle: string
    currentPipelineId: number
    onClose: () => void
    onMoved: () => void
}

/** Etapa aberta: nem de ganho nem de perda. É onde uma oportunidade viva deve entrar. */
const isOpenStage = (stage: { isWonStage?: boolean; isLostStage?: boolean }) => !stage.isWonStage && !stage.isLostStage

/**
 * Move a oportunidade para OUTRO funil. Lista só os funis que o usuário enxerga
 * (o backend já recorta pela visibilidade) e sugere a primeira etapa aberta do
 * funil escolhido. Funil com dona: ela assume a oportunidade
 * (docs/sales-pipeline-distribution.md).
 *
 * A CARTEIRA do cliente é uma escolha, não uma consequência: o cliente de consulta
 * não precisa mudar de dona só porque pediu uma cirurgia, e a nova dona vê a
 * conversa pelo funil da oportunidade de qualquer jeito. Vem marcada porque é o
 * que a operação fazia até aqui.
 */
export default function MoveDealPipelineModal({
    dealIds,
    dealTitle,
    currentPipelineId,
    onClose,
    onMoved,
}: MoveDealPipelineModalProps) {
    // Destinos, não os funis que a pessoa acompanha: quem pode mover precisa enxergar
    // para onde, mesmo sem ver aquele funil no quadro.
    const [pipelines, setPipelines] = useState<PipelineMoveTarget[]>([])
    const [loading, setLoading] = useState(true)
    const [pipelineId, setPipelineId] = useState<number | ''>('')
    const [stageId, setStageId] = useState<number | ''>('')
    const [transferWallet, setTransferWallet] = useState(true)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
        getPipelineMoveTargets()
            .then(res => {
                if (res.success) {
                    setPipelines(res.result.filter(p => p.id !== currentPipelineId))
                } else {
                    toast.error(res.error || 'Não foi possível carregar os funis')
                }
            })
            .finally(() => setLoading(false))
    }, [currentPipelineId])

    const stages = useMemo(
        () => pipelines.find(p => p.id === pipelineId)?.stages ?? [],
        [pipelines, pipelineId],
    )

    /**
     * Destinos agrupados por unidade.
     *
     * A lista deixou de ser recortada pela unidade do usuário (30/09/2026), então numa
     * operação com três unidades ela passou de 7 para 11 funis, com nomes parecidos entre
     * si. Sem o agrupamento, escolher vira adivinhação — e escolher errado manda o caso
     * para a equipe errada.
     *
     * Os funis da organização (sem unidade) vêm primeiro: são os de triagem, onde o
     * contato novo cai.
     */
    const groups = useMemo(() => {
        const byCompany = new Map<string, PipelineMoveTarget[]>()
        for (const pipeline of pipelines) {
            const label = pipeline.company?.name ?? 'Toda a organização'
            const list = byCompany.get(label)
            if (list) list.push(pipeline)
            else byCompany.set(label, [pipeline])
        }
        return [...byCompany.entries()].sort(([a], [b]) =>
            a === 'Toda a organização' ? -1 : b === 'Toda a organização' ? 1 : a.localeCompare(b),
        )
    }, [pipelines])

    const handlePipelineChange = (value: string) => {
        const id = value ? Number(value) : ''
        setPipelineId(id)
        const target = pipelines.find(p => p.id === id)
        const firstOpen = target?.stages?.find(isOpenStage) ?? target?.stages?.[0]
        setStageId(firstOpen?.id ?? '')
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!pipelineId || !stageId) {
            toast.error('Escolha o funil e a etapa de destino')
            return
        }
        setSaving(true)
        try {
            const results = await Promise.all(
                dealIds.map(id => moveDealStage(id, { pipelineId, stageId, transferWallet })),
            )
            const failed = results.find(r => !r.success)
            if (failed) {
                toast.error(failed.error || 'Não foi possível mover a oportunidade')
                return
            }
            const target = pipelines.find(p => p.id === pipelineId)
            toast.success(
                dealIds.length > 1
                    ? `${dealIds.length} oportunidades movidas para ${target?.name ?? 'o funil escolhido'}`
                    : `Oportunidade movida para ${target?.name ?? 'o funil escolhido'}`,
            )
            onMoved()
        } finally {
            setSaving(false)
        }
    }

    return (
        <dialog className="modal modal-open z-50">
            <form className="modal-box !max-w-lg" onSubmit={handleSubmit}>
                <div className="flex items-center justify-between mb-1">
                    <h3 className="text-lg font-bold">Mover para outro funil</h3>
                    <button type="button" className="btn btn-ghost btn-sm btn-circle" onClick={onClose}>
                        <MdClose className="text-lg" />
                    </button>
                </div>
                <p className="text-sm text-base-content/60 mb-5 truncate" title={dealTitle}>
                    {dealTitle}
                    {dealIds.length > 1 && ` · ${dealIds.length} oportunidades`}
                </p>

                {loading ? (
                    <div className="flex justify-center py-8">
                        <span className="loading loading-spinner loading-md text-secondary" />
                    </div>
                ) : pipelines.length === 0 ? (
                    <p className="text-sm text-base-content/60 py-4">Não há outro funil disponível para você.</p>
                ) : (
                    <div className="space-y-4">
                        <div className="form-control w-full">
                            <label className="label py-1" htmlFor="move-deal-pipeline">
                                <span className="label-text text-sm font-medium">Funil de destino</span>
                            </label>
                            <select
                                id="move-deal-pipeline"
                                className="select select-bordered w-full"
                                value={pipelineId}
                                onChange={e => handlePipelineChange(e.target.value)}
                                disabled={saving}
                            >
                                <option value="">Selecione…</option>
                                {groups.map(([label, items]) => (
                                    <optgroup key={label} label={label}>
                                        {items.map(p => (
                                            <option key={p.id} value={p.id}>
                                                {p.name}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full">
                            <label className="label py-1" htmlFor="move-deal-stage">
                                <span className="label-text text-sm font-medium">Etapa</span>
                            </label>
                            <select
                                id="move-deal-stage"
                                className="select select-bordered w-full"
                                value={stageId}
                                onChange={e => setStageId(e.target.value ? Number(e.target.value) : '')}
                                disabled={saving || !pipelineId}
                            >
                                <option value="">{pipelineId ? 'Selecione…' : 'Escolha o funil primeiro'}</option>
                                {stages.map(s => (
                                    <option key={s.id} value={s.id}>
                                        {s.name}
                                        {s.isWonStage ? ' (ganho)' : s.isLostStage ? ' (perda)' : ''}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-control w-full pt-1">
                            <label
                                className="label cursor-pointer justify-start gap-3 py-2"
                                htmlFor="move-deal-transfer-wallet"
                            >
                                <input
                                    id="move-deal-transfer-wallet"
                                    type="checkbox"
                                    className="checkbox checkbox-secondary checkbox-sm"
                                    checked={transferWallet}
                                    onChange={e => setTransferWallet(e.target.checked)}
                                    disabled={saving}
                                />
                                <span className="label-text text-sm font-medium">
                                    Mover o cliente para a carteira da nova dona
                                </span>
                            </label>
                            <p className="text-xs text-base-content/50 pl-9">
                                {transferWallet
                                    ? 'O cliente sai da carteira em que está e passa a ser atendido pela dona do funil de destino.'
                                    : 'O cliente fica na carteira em que está. A dona do funil de destino vê a conversa pela oportunidade, sem assumir o relacionamento.'}
                            </p>
                        </div>

                        <p className="text-xs text-base-content/50">
                            Se o funil tem dona, ela passa a ser a responsável pela oportunidade e
                            pelo lead. Movendo para outra unidade, a oportunidade passa a ser dela —
                            e some da sua lista.
                        </p>
                    </div>
                )}

                <div className="modal-action">
                    <button type="button" className="btn btn-ghost" onClick={onClose} disabled={saving}>
                        Cancelar
                    </button>
                    <button
                        type="submit"
                        className="btn btn-secondary gap-2"
                        disabled={saving || !pipelineId || !stageId}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm" />
                        ) : (
                            <MdDriveFileMove className="text-lg" />
                        )}
                        Mover
                    </button>
                </div>
            </form>
            <form method="dialog" className="modal-backdrop">
                <button type="button" onClick={onClose}>
                    close
                </button>
            </form>
        </dialog>
    )
}
